import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbModal, NgbNavModule, NgbPaginationModule } from '@ng-bootstrap/ng-bootstrap';
import { PanelComponent } from '@components/core-template/panel/panel.component';
import { RequestService } from '@services/request.service';
import { UserService } from '@services/core/user.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ReviewRequestComponent } from './review-request/review-request.component';
import { NgSelectComponent } from '@ng-select/ng-select';
import { canCancelRequest } from 'app/utils/role-rules';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-requests',
  standalone: true,
  imports: [ReviewRequestComponent, NgbNavModule, CommonModule, FormsModule, NgbPaginationModule, NgSelectComponent],
  providers: [RequestService],
  templateUrl: './requests.component.html',
  styleUrls: ['./requests.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RequestsComponent implements OnInit {

  private destroyRef = inject(DestroyRef)
  private cdr = inject(ChangeDetectorRef)
  protected _requestService: RequestService = inject(RequestService)
  protected modalService: NgbModal = inject(NgbModal)
  protected _userService: UserService = inject(UserService)


  protected listMovements: any[] = []
  protected listRequests = []
  protected listRequestsTemp = []
  protected requestSel: any
  protected page: number = 1
  protected totalResult: number = 0
  protected totalPages: number = 1
  protected searchByFilter = ['Nombre', 'RUT', 'Póliza']
  protected params = {
    movementId: null,
    search: null,
    searchBy: 'Nombre',
    page: 1,
    pageSize: 10,
    since: null,
    until: null,
  }

  public type: any

  public pageSize: number = 10
  public date_start: Date
  public date_until: Date
  public modalOptions = {
    size: 'lg',
    scrollable: true,
    animation: true,
    backdrop: true
  }
  public closeResult = ""
  public search = ""

  protected Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 2000,
    timerProgressBar: true
  })

  ngOnInit(): void {
    this.getTypeMovement()
  }

  open(content: any) {
    this.modalService.open(content, this.modalOptions).result.then((result) => {
      this.closeResult = `Closed with: ${result}`;
    }, (reason) => {
      this.closeResult = `${reason}`;
    });
  }

  getTypeMovement() {
    this._requestService.getTypeMovements().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        this.listMovements = res
        this.params.movementId = res[0]._id
        this.getRequest()
        this.cdr.markForCheck()
      },
      error: (err) => { console.error('Error loading types', err); this.Toast.fire({ title: 'Error al cargar tipos', icon: 'error' }) }
    })
  }

  // setFormatRUT(){

  // }

  getRequest() {
    Swal.fire({
      text: "Buscando Datos...",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    })
    this.listRequests = []

    this._requestService.getRequests(this.params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        if (res.requests.length > 0) {
          this.listRequests = res.requests
          this.listRequestsTemp = this.listRequests
          this.totalResult = res.total
          this.totalPages = Math.ceil(this.totalResult / this.params.pageSize)
        }
        Swal.close()
        this.cdr.markForCheck()
      },
      error: (err) => { console.error('Error loading requests', err); Swal.close(); this.Toast.fire({ title: 'Error al cargar solicitudes', icon: 'error' }) }
    })
  }

  setRequest(request: any, modal: any) {
    this.requestSel = request
    this.open(modal)
  }

  findSearch(actual?: number) {
    if (actual) this.page = actual
    this.params.page = this.page
    this.params.search = this.params.search || null
    this.getRequest()
  }

  reject(request: any) {
    const isCancelled = request.state?.stage === 0
    Swal.fire({
      title: isCancelled ? '¿Eliminar solicitud definitivamente?' : '¿Cancelar solicitud?',
      text: isCancelled
        ? `Se eliminará permanentemente la solicitud de ${request.insured?.names} ${request.insured?.surname}`
        : `Se cancelará la solicitud de ${request.insured?.names} ${request.insured?.surname}`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: isCancelled ? 'Sí, eliminar' : 'Sí, cancelar',
      cancelButtonText: 'No'
    }).then(result => {
      if (result.isConfirmed) {
        const action = isCancelled
          ? this._requestService.forceDelete(request._id)
          : this._requestService.cancel(request)
        action.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: (res) => {
            this.Toast.fire({ title: res.msg, icon: 'success' })
            this.getRequest()
          },
          error: () => {
            this.Toast.fire({ title: `Error al ${isCancelled ? 'eliminar' : 'cancelar'} la solicitud`, icon: 'error' })
          }
        })
      }
    })
  }

  protected canCancel(request: any): boolean {
    const stage = request?.state?.stage
    return canCancelRequest((this._userService.sharingRole() as any)?.priority, stage) || stage === 0
  }

  private _downloadBlob(res: Blob, filename: string): void {
    const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    window.URL.revokeObjectURL(url)
  }

  exportRequests() {
    const movement = this.listMovements.find(m => m._id === this.params.movementId)
    if (!movement) return

    const endpoints: any = {
      0: 'report/excel/incorporation',
      1: 'report/excel/transfer-policy',
      2: 'report/excel/transfer-group',
      3: 'report/excel/modify-capital',
      4: 'report/excel/exclution'
    }
    const endpoint = endpoints[movement.priority]
    if (!endpoint) return

    Swal.fire({
      text: "Generando Archivo...",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    })
    this._requestService.generateExcel(endpoint, {
      since: this.params.since,
      until: this.params.until
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res: Blob) => {
      const date = new Date()
      this._downloadBlob(res, movement.name + "-" + date.toISOString() + '.xlsx')
      Swal.close()
    })
  }
}
